import React, { useEffect, useState } from "react";
import { apiRequest } from "../api/client.js";
import {
  ArrowLeft,
  Check,
  Edit,
  MapPin,
  Settings,
  Share2,
  Target,
  Timer,
  Trash2,
  UserCheck,
  UserPlus,
  X
} from "../shared/icons.jsx";
import { Avatar, Button, Field, LinkButton, MonoLabel, ProgressBar, cx, initialsOf } from "../shared/ui.jsx";
import { findWorkout, runnableWorkouts } from "../shared/workouts.js";
import { EVENT_TYPE_LABELS, OFFICIAL_TEST_NOTES, OFFICIAL_TEST_TITLES, clockTime, durationLabel, toDate, toInputValue } from "../shared/dates.js";
import { WX, HeatAdvisoryChip, useForecast, pickForecast } from "../shared/weather.jsx";

// Attendance colour coding: green present, red absent, yellow excused.
const ATTN_TEXT = { present: "text-green-700", absent: "text-red-700", excused: "text-amber-600" };
const ATTN_ACTIVE = {
  present: "border-green-600 bg-green-600 text-white",
  absent: "border-red-600 bg-red-600 text-white",
  excused: "border-amber-500 bg-amber-500 text-white"
};

const hasRole = (principal, predicate) =>
  principal?.kind === "staff" && principal.roles.some(predicate);

const canManageEvent = (principal, event) =>
  hasRole(principal, (role) =>
    role.role === "platform_admin" ||
    (role.role === "squadron_admin" && role.squadron_id === event.squadronId) ||
    (role.role === "flight_admin" && role.flight_id === event.ownerFlightId) ||
    (role.role === "leader" && role.flight_id === event.ownerFlightId && event.createdByUserId === principal.id)
  );

const canOperateMockEvent = (principal, event) =>
  hasRole(principal, (role) =>
    role.role === "platform_admin" ||
    (role.role === "squadron_admin" && role.squadron_id === event.squadronId) ||
    (["flight_admin", "leader"].includes(role.role) && role.flight_id === event.ownerFlightId)
  );

const canMarkRegistration = (principal, event, registration) =>
  hasRole(principal, (role) =>
    role.role === "platform_admin" ||
    (role.role === "squadron_admin" && role.squadron_id === event.squadronId) ||
    role.flight_id === event.ownerFlightId ||
    role.flight_id === registration.home_flight_id
  );

// Forecast for the session, via Open-Meteo (free, no key, no dependency). Uses the
// squadron's admin-set coordinates (see UnitManager). Within 72h it shows the outlook
// at the session's exact hour; further out it shows that day's hi/lo range. Renders
// nothing if no weather location is set, the session is in the past or beyond the
// 10-day horizon, or the API is unreachable.
function WeatherStrip({ weather, startsAt }) {
  const forecast = useForecast(weather);
  const wx = pickForecast(forecast, startsAt);
  if (!wx) return null;
  const [label, icon] = WX[wx.code] || ["—", "🌡️"];
  return (
    <div className="bg-surface px-4 py-3 flex items-center gap-3 border-t border-line">
      <span className="text-xl leading-none">{icon}</span>
      <div className="flex-1">
        <MonoLabel className="text-[9px] block mb-0.5">Forecast{wx.kind === "hour" ? ` · ${wx.time}` : ""}{weather?.place ? ` · ${weather.place}` : ""}</MonoLabel>
        <div className="text-[15px] font-semibold text-ink">
          {wx.kind === "hour" ? `${wx.temp}°F` : `${wx.hi}° / ${wx.lo}°F`} · {label}
        </div>
        <div className="mt-1.5"><HeatAdvisoryChip wx={wx} full /></div>
      </div>
      {wx.precip != null && (
        <div className="mono text-[10px] text-muted text-right leading-tight">{wx.precip}%<br />precip</div>
      )}
    </div>
  );
}

// Staff roster-picker: add existing flight members to the event in one step,
// instead of re-typing names (which spawns duplicate provisional members).
function RosterPicker({ event, onClose, onAdded }) {
  const [members, setMembers] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiRequest("flight_members", { query: { flightId: event.ownerFlightId, eventId: event.id } })
      .then((r) => setMembers(r.members))
      .catch((e) => setError(e.message));
  }, [event.id, event.ownerFlightId]);

  const addable = (members || []).filter((m) => !m.registered);
  const allOn = addable.length > 0 && addable.every((m) => selected.has(m.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(addable.map((m) => m.id)));

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await apiRequest("register_roster", { method: "POST", body: { eventId: event.id, memberIds: [...selected] } });
      await onAdded();
      onClose();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 p-4 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white p-5 w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h2 className="display text-2xl text-ink m-0">Add from roster</h2>
          <button type="button" onClick={onClose} className="text-stone-600 bg-transparent border-0 cursor-pointer p-1" aria-label="Close"><X size={18} /></button>
        </div>
        {error && <div className="border border-[#c98b8b] bg-[rgba(176,86,58,0.1)] text-red-800 p-2.5 text-sm mb-3">{error}</div>}
        {members === null ? (
          <div className="text-muted mono text-xs uppercase tracking-[0.12em] py-6">Loading roster…</div>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted m-0 py-4">No active members in this flight. Approve members to build the roster, or register by name below.</p>
        ) : (
          <>
            <label className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.1em] text-ink cursor-pointer pb-2 mb-1 border-b border-line">
              <input type="checkbox" className="w-4 h-4 accent-[#d4682a]" checked={allOn} onChange={toggleAll} disabled={addable.length === 0} />
              Select all ({addable.length})
            </label>
            <div className="overflow-y-auto flex-1 -mx-1">
              {members.map((m) => (
                <label key={m.id} className={cx("px-1 py-2 flex items-center gap-3 text-sm", m.registered ? "text-muted" : "text-ink cursor-pointer")}>
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#d4682a]"
                    disabled={m.registered}
                    checked={m.registered || selected.has(m.id)}
                    onChange={() => toggle(m.id)}
                  />
                  <span className="flex-1">{m.name}</span>
                  {m.registered && <span className="mono text-[9px] uppercase tracking-[0.12em] text-pass-deep">On roster</span>}
                </label>
              ))}
            </div>
            <Button className="w-full mt-3" disabled={busy || selected.size === 0} onClick={submit}>
              Add {selected.size || ""} to event
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function SessionControlButton({ icon: ControlIcon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 bg-white border-0 px-2 py-3.5 cursor-pointer flex flex-col items-center gap-1.5 text-ink hover:bg-line-soft"
    >
      <ControlIcon size={20} />
      <span className="mono text-[9px] uppercase tracking-[0.1em]">{label}</span>
    </button>
  );
}

export default function EventDetail({
  event,
  principal,
  ownedRegistrations,
  onBack,
  onRegister,
  onRemove,
  onAttendance,
  onBulkAttendance,
  onSave,
  onDeleteEvent,
  onDeleteSeries,
  onShare,
  onPush,
  onMockTool,
  onWorkout,
  onRosterAdded
}) {
  const [editing, setEditing] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [pickRoster, setPickRoster] = useState(false);
  const [draft, setDraft] = useState(null);
  const [applySeries, setApplySeries] = useState(false);

  const isStaff = principal.kind === "staff";
  const canceled = event.status === "canceled";
  const canManage = canManageEvent(principal, event);
  const canOperateMock = canOperateMockEvent(principal, event);
  const isSquadronAdmin = hasRole(principal, (role) =>
    role.role === "platform_admin" || (role.role === "squadron_admin" && role.squadron_id === event.squadronId)
  );
  const owned = ownedRegistrations[event.id] || [];
  const eventWorkout = findWorkout(event.workoutId);
  const runnableWorkout = eventWorkout?.segments?.length ? eventWorkout : null;

  const startsAt = toDate(event.startsAt);
  const heroDate = startsAt
    .toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })
    .toUpperCase();

  const startEdit = () => {
    setDraft({
      title: event.title,
      startsAt: toInputValue(event.startsAt),
      durationMinutes: event.durationMinutes ?? "",
      capacity: event.capacity ?? "",
      leadName: event.leadName || "",
      location: event.location,
      eventType: event.eventType,
      notes: event.notes,
      workoutId: event.workoutId || ""
    });
    setEditing(true);
  };

  const saveDraft = async () => {
    const ok = await onSave({
      eventId: event.id,
      ownerFlightId: event.ownerFlightId,
      ...draft,
      durationMinutes: draft.durationMinutes === "" ? null : Number(draft.durationMinutes),
      capacity: draft.capacity === "" ? null : Number(draft.capacity),
      status: event.status,
      revision: event.revision,
      series: applySeries && Boolean(event.seriesId),
      seriesId: event.seriesId
    });
    if (ok) { setEditing(false); setApplySeries(false); }
  };

  const deleteEvent = async () => {
    if (!window.confirm("Delete this event? It will be removed from the calendar. This cannot be undone.")) return;
    const ok = await onDeleteEvent(event.id);
    if (ok) onBack();
  };

  const deleteSeries = async (scope) => {
    const msg = scope === "all"
      ? "Delete the ENTIRE recurring series, including past events? This cannot be undone."
      : "Delete all upcoming events in this recurring series? Past events are kept.";
    if (!window.confirm(msg)) return;
    const ok = await onDeleteSeries(event.seriesId, scope);
    if (ok) onBack();
  };

  const setStatus = (status) =>
    onSave({
      eventId: event.id,
      ownerFlightId: event.ownerFlightId,
      title: event.title,
      startsAt: toInputValue(event.startsAt),
      durationMinutes: event.durationMinutes ?? null,
      capacity: event.capacity ?? null,
      leadName: event.leadName || "",
      location: event.location,
      eventType: event.eventType,
      notes: event.notes,
      workoutId: event.workoutId ?? null,
      status,
      revision: event.revision
    });

  const registrations = event.registrations || [];
  const rosterAvatars = registrations.slice(0, 6);

  return (
    <div className="flex-1 flex flex-col w-full max-w-screen-sm mx-auto anim-push-in" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* top bar */}
      <div className="sticky top-0 z-20 bg-ink">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => { setEditing(false); onBack(); }}
            className="flex items-center gap-2 bg-transparent border-0 text-white mono text-[11px] uppercase tracking-[0.12em] cursor-pointer p-1"
          >
            <ArrowLeft size={18} />Calendar
          </button>
          <span className="flex items-center gap-4">
            {canManage && !editing && (
              <button
                onClick={() => onShare(event)}
                aria-label="Share event"
                className="bg-transparent border-0 text-muted hover:text-white cursor-pointer p-1 flex"
              >
                <Share2 size={19} />
              </button>
            )}
            {canManage && !editing && (
              <button
                onClick={startEdit}
                className="flex items-center gap-1.5 bg-transparent border border-ink-line px-2.5 py-1.5 text-white mono text-[9px] font-semibold uppercase tracking-[0.14em] cursor-pointer"
              >
                <Edit size={13} className="text-signal-bright" />Edit
              </button>
            )}
            {editing && (
              <span className="flex items-center gap-1.5 mono text-[9px] font-semibold uppercase tracking-[0.16em] text-signal-bright">
                <span className="w-1.5 h-1.5 bg-signal-bright" />Editing
              </span>
            )}
          </span>
        </div>
      </div>

      {/* scroll body */}
      <div className="flex-1">
        {canceled && (
          <div className="bg-[#7a1f1f] px-5 py-3 flex items-center gap-2.5">
            <X size={17} className="text-white flex-shrink-0" />
            <span className="mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white">
              Event canceled — registration closed
            </span>
          </div>
        )}

        {/* hero */}
        <div className="bg-ink px-5 pt-4 pb-5">
          <div className="flex justify-between items-center mb-3">
            <span className="mono text-[10px] uppercase tracking-[0.16em] text-signal-bright">
              {EVENT_TYPE_LABELS[event.eventType] || "Event"}
            </span>
            <span className="mono text-[10px] uppercase tracking-[0.12em] text-muted">{event.ownerFlightName}</span>
          </div>
          {editing && draft.eventType === "OFFICIAL_TEST" ? (
            <select
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full box-border display text-[42px] leading-none text-ink bg-white border-0 outline outline-2 outline-signal px-3 py-1 mb-4"
            >
              {OFFICIAL_TEST_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : editing ? (
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full box-border display text-[42px] leading-none text-ink bg-white border-0 outline outline-2 outline-signal px-3 py-1 mb-4"
            />
          ) : (
            <h2 className="display text-[50px] leading-[0.86] tracking-[0.01em] text-white m-0 mb-4">{event.title}</h2>
          )}
          {editing ? (
            <div className="flex flex-col gap-2.5">
              <Field
                dark
                type="datetime-local"
                value={draft.startsAt}
                onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2.5">
                <Field
                  dark
                  type="number"
                  min="5"
                  max="1440"
                  placeholder="Duration (minutes)"
                  value={draft.durationMinutes}
                  onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value })}
                />
                <Field
                  dark
                  type="number"
                  min="1"
                  max="1000"
                  placeholder="Capacity"
                  value={draft.capacity}
                  onChange={(e) => setDraft({ ...draft, capacity: e.target.value })}
                />
              </div>
            </div>
          ) : (
            <div className="flex gap-2.5">
              <div className="bg-signal text-ink px-3.5 py-2.5 text-center">
                <div className="mono text-[9px] font-semibold tracking-[0.16em]">{heroDate}</div>
                <div className="display text-3xl leading-[0.82] mt-1">{clockTime(startsAt)}</div>
              </div>
              {durationLabel(event.durationMinutes) && (
                <div className="border border-ink-line text-white px-3.5 py-2.5 text-center">
                  <div className="mono text-[9px] font-semibold tracking-[0.16em] text-muted">DURATION</div>
                  <div className="display text-3xl leading-[0.82] mt-1">{durationLabel(event.durationMinutes)}</div>
                </div>
              )}
              <div className="border border-ink-line text-white px-3.5 py-2.5 text-center">
                <div className="mono text-[9px] font-semibold tracking-[0.16em] text-muted">TURNOUT</div>
                <div className="display text-3xl leading-[0.82] mt-1">
                  {event.capacity > 0 ? `${event.attendanceCount}/${event.capacity}` : event.attendanceCount}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* meta grid */}
        <div className="grid grid-cols-2 gap-px bg-line">
          <div className="bg-surface px-4 py-3.5">
            <MonoLabel className="text-[9px] block mb-1">Location</MonoLabel>
            {editing ? (
              <Field value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="Location" />
            ) : (
              <div className="text-[15px] font-semibold text-ink flex items-center gap-2">
                <MapPin size={14} className="text-muted flex-shrink-0" />
                {event.location || "Location pending"}
              </div>
            )}
          </div>
          <div className="bg-surface px-4 py-3.5">
            <MonoLabel className="text-[9px] block mb-1">{editing ? "Type" : "Session lead"}</MonoLabel>
            {editing ? (
              <Field
                as="select"
                value={draft.eventType}
                onChange={(e) => {
                  const eventType = e.target.value;
                  setDraft(eventType === "OFFICIAL_TEST"
                    ? {
                        ...draft,
                        eventType,
                        title: OFFICIAL_TEST_TITLES.includes(draft.title) ? draft.title : OFFICIAL_TEST_TITLES[0],
                        notes: draft.notes || OFFICIAL_TEST_NOTES
                      }
                    : { ...draft, eventType });
                }}
              >
                <option value="PT">Physical Training</option>
                <option value="MOCK_TEST">Mock PT Test</option>
                <option value="OFFICIAL_TEST">Official PT Test</option>
                <option value="OTHER">Other</option>
              </Field>
            ) : (
              <div className="text-[15px] font-semibold text-ink">{event.leadName || "—"}</div>
            )}
          </div>
          {editing && (
            <div className="bg-surface px-4 py-3.5 col-span-2">
              <MonoLabel className="text-[9px] block mb-1">Session lead</MonoLabel>
              <Field
                placeholder="Session lead (optional)"
                value={draft.leadName}
                onChange={(e) => setDraft({ ...draft, leadName: e.target.value })}
              />
            </div>
          )}
          {editing && (
            <div className="bg-surface px-4 py-3.5 col-span-2">
              <MonoLabel className="text-[9px] block mb-1">Guided workout</MonoLabel>
              <Field as="select" value={draft.workoutId} onChange={(e) => setDraft({ ...draft, workoutId: e.target.value })}>
                <option value="">None (no guided player)</option>
                {runnableWorkouts.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
              </Field>
            </div>
          )}
        </div>

        {!editing && <WeatherStrip weather={event.weather} startsAt={event.startsAt} />}

        {/* turnout */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex justify-between items-end mb-2.5">
            <span className="mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink">Turnout</span>
            <span className="display text-2xl leading-[0.8] text-ink">
              {event.attendanceCount}
              <span className="mono text-[11px] text-muted">
                {event.capacity > 0
                  ? ` / ${event.capacity}${event.attendanceCount >= event.capacity ? " · Full" : ""}`
                  : " registered"}
              </span>
            </span>
          </div>
          {event.capacity > 0 && (
            <ProgressBar
              value={event.attendanceCount}
              max={event.capacity}
              full={event.attendanceCount >= event.capacity}
              className="h-1.5 mb-3.5"
            />
          )}
          {isStaff && registrations.length > 0 ? (
            <div className="flex items-center">
              {rosterAvatars.map((registration, index) => (
                <Avatar
                  key={registration.id}
                  initials={initialsOf(registration.staff_display_name || registration.participant_name)}
                  size={32}
                  tone={index % 3 === 0 ? "signal" : index % 3 === 1 ? "dark" : "light"}
                  className="-mr-2 border-2 border-surface"
                />
              ))}
              {registrations.length > rosterAvatars.length && (
                <span className="ml-4 mono text-[11px] text-stone-600">+{registrations.length - rosterAvatars.length} more</span>
              )}
            </div>
          ) : !isStaff ? (
            <div className="mono text-[11px] text-stone-600">{event.attendanceCount} going</div>
          ) : (
            <div className="mono text-[11px] text-muted">No registrations yet.</div>
          )}
          {owned.length > 0 && (
            <div className="mt-3">
              <MonoLabel className="text-[9px]">Added on this device</MonoLabel>
              {owned.map((registration) => (
                <div key={registration.registrationId} className="flex justify-between items-center mt-2 text-sm text-ink">
                  <span>{registration.participantName}</span>
                  <LinkButton onClick={() => onRemove(registration)}>Remove</LinkButton>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* brief */}
        <div className="px-5 pt-3 pb-4">
          <MonoLabel className="text-[9px] block mb-1.5">Brief</MonoLabel>
          {editing ? (
            <Field
              as="textarea"
              rows={4}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Notes for participants"
            />
          ) : (
            <p className="text-sm leading-relaxed text-stone-600 m-0 whitespace-pre-wrap">
              {event.notes || "No brief posted."}
            </p>
          )}
        </div>

        {/* session control */}
        {isStaff && !editing && (
          <div className="mx-5 mb-5 border border-line bg-white">
            <div className="flex items-center gap-2 px-3.5 py-2.5 bg-ink">
              <Settings size={14} className="text-signal-bright" />
              <span className="mono text-[10px] uppercase tracking-[0.16em] text-white">Session control</span>
              <span className="ml-auto mono text-[8px] uppercase tracking-[0.12em] text-signal-bright border border-signal-deep px-1.5 py-0.5">
                Staff only
              </span>
            </div>
            <div className="flex gap-px bg-line">
              <SessionControlButton icon={UserCheck} label="Attendance" onClick={() => setShowRoster((value) => !value)} />
              {canOperateMock && !canceled && (
                <SessionControlButton icon={UserPlus} label="Add roster" onClick={() => setPickRoster(true)} />
              )}
              {event.eventType === "MOCK_TEST" && canOperateMock && (
                <SessionControlButton icon={Timer} label="Run test" onClick={() => onMockTool({ mode: "heat", event })} />
              )}
              {runnableWorkout && canOperateMock && (
                <SessionControlButton icon={Target} label="Start workout" onClick={() => onWorkout({ event })} />
              )}
              {canManage && <SessionControlButton icon={Edit} label="Edit details" onClick={startEdit} />}
              {isSquadronAdmin && <SessionControlButton icon={Share2} label="Push sq-wide" onClick={() => onPush(event.id)} />}
            </div>
          </div>
        )}

        {/* roster / attendance marking */}
        {isStaff && showRoster && !editing && (
          <div className="mx-5 mb-6 bg-white">
            <div className="px-4 py-2.5 border-b border-line-soft flex items-center justify-between gap-2">
              <MonoLabel className="text-[9px]">Roster · mark attendance</MonoLabel>
              {(() => {
                const markable = registrations.filter((r) => canMarkRegistration(principal, event, r));
                if (markable.length === 0) return null;
                const unmarked = markable.filter((r) => !r.attendance);
                return (
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onBulkAttendance(markable.map((r) => r.id), "present")}
                      className="mono text-[9px] font-semibold uppercase border border-green-600 text-green-700 px-2 py-1 bg-transparent cursor-pointer"
                    >
                      All present
                    </button>
                    <button
                      onClick={() => onBulkAttendance(unmarked.map((r) => r.id), "absent")}
                      disabled={unmarked.length === 0}
                      className="mono text-[9px] font-semibold uppercase border border-line text-stone-600 px-2 py-1 bg-transparent cursor-pointer disabled:opacity-40"
                    >
                      Rest absent
                    </button>
                  </div>
                );
              })()}
            </div>
            {registrations.length === 0 ? (
              <p className="text-sm text-muted px-4 py-3 m-0">No registrations.</p>
            ) : registrations.map((registration) => (
              <div key={registration.id} className="px-4 py-2.5 border-b border-line-soft last:border-b-0 flex flex-wrap justify-between items-center gap-2">
                <div className="min-w-0">
                  <span className="text-sm text-ink">{[registration.rank_snapshot, registration.staff_display_name || registration.participant_name].filter(Boolean).join(" ")}</span>
                  {registration.guest && <span className="ml-2 mono text-[9px] uppercase text-violet-700">Guest: {registration.home_flight_name}</span>}
                  {registration.attendance && (
                    <span className={cx("ml-2 mono text-[9px] uppercase", ATTN_TEXT[registration.attendance.status] || "text-pass-deep")}>
                      {registration.attendance.status} ({registration.attendance.authority === "home" ? registration.home_flight_name : event.ownerFlightName})
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  {event.eventType === "MOCK_TEST" && canOperateMock && (
                    <button
                      onClick={() => onMockTool({ mode: "score", event, registration })}
                      className="mono text-[9px] font-semibold uppercase border border-signal text-signal-deep px-2 py-1 bg-transparent cursor-pointer"
                    >
                      Score
                    </button>
                  )}
                  {canMarkRegistration(principal, event, registration) && ["present", "absent", "excused"].map((status) => (
                    <button
                      key={status}
                      onClick={() => onAttendance(registration.id, registration.attendance?.status === status ? null : status)}
                      className={cx(
                        "mono text-[9px] uppercase border px-2 py-1 bg-transparent cursor-pointer",
                        registration.attendance?.status === status
                          ? ATTN_ACTIVE[status]
                          : "border-line text-stone-600 hover:border-ink hover:text-ink"
                      )}
                    >
                      {status[0]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* sticky bottom CTA */}
      {(editing || canceled || principal.kind === "member") && (
        <div className="sticky bottom-0 bg-white border-t border-line px-4 pt-3.5 pb-[calc(1.1rem+env(safe-area-inset-bottom))]">
          {editing ? (
            <>
              {event.seriesId && (
                <label className="flex items-center gap-2 mb-2.5 mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink cursor-pointer">
                  <input type="checkbox" checked={applySeries} onChange={(e) => setApplySeries(e.target.checked)} />
                  Apply to all upcoming in this series
                </label>
              )}
              {canManage && !canceled && (
                <Button variant="danger" className="w-full mb-2.5 h-11" onClick={() => { setEditing(false); setStatus("canceled"); }}>
                  <X size={15} />Cancel this event
                </Button>
              )}
              {canManage && (
                <Button variant="danger" className="w-full mb-2.5 h-11" onClick={deleteEvent}>
                  <Trash2 size={15} />Delete event
                </Button>
              )}
              {event.seriesId && canManage && (
                <>
                  <Button variant="danger" className="w-full mb-2.5 h-11" onClick={() => deleteSeries("future")}>
                    <X size={15} />Delete upcoming series
                  </Button>
                  <Button variant="danger" className="w-full mb-2.5 h-11" onClick={() => deleteSeries("all")}>
                    <X size={15} />Delete entire series
                  </Button>
                </>
              )}
              <div className="flex gap-2.5">
                <Button variant="outline" size="lg" className="flex-[0_0_38%]" onClick={() => setEditing(false)}>Discard</Button>
                <Button size="lg" className="flex-1" onClick={saveDraft}>Save changes</Button>
              </div>
            </>
          ) : canceled ? (
            <div className="flex items-center gap-2.5">
              <div className="flex-1 h-[52px] flex items-center justify-center bg-line-soft text-muted mono text-xs font-semibold uppercase tracking-[0.14em]">
                Registration closed
              </div>
              {canManage && (
                <Button variant="dark" size="lg" onClick={() => setStatus("scheduled")}>Restore</Button>
              )}
            </div>
          ) : event.memberRegistrationId ? (
            <div className="flex items-center justify-between h-[52px] px-4 border-[1.5px] border-signal">
              <span className="flex items-center gap-2 mono text-xs font-semibold uppercase tracking-[0.1em] text-signal-deep">
                <Check size={16} />You're registered
              </span>
              <LinkButton onClick={() => onRemove({ registrationId: event.memberRegistrationId })}>Remove</LinkButton>
            </div>
          ) : event.registrationLocked ? (
            <div className="flex-1 h-[52px] flex items-center justify-center bg-line-soft text-muted mono text-xs font-semibold uppercase tracking-[0.14em]">
              Registration closed
            </div>
          ) : (
            <Button size="lg" className="w-full" onClick={() => onRegister(event.id)}>Register</Button>
          )}
        </div>
      )}

      {pickRoster && (
        <RosterPicker
          event={event}
          onClose={() => setPickRoster(false)}
          onAdded={onRosterAdded}
        />
      )}
    </div>
  );
}
